// src/components/order/OrdersTable.tsx
//
// OrdersTable (Standard)
// - Top: StandardTableToolbar (Total + Page Jump)
// - Middle: StandardTableFrame
// - Bottom: StandardPagination
// - Partner view hides the Stock Code column.
//
// Navigation policy: Order Code navigates to the order detail page.

import React, { useCallback, useMemo } from 'react';
import { Box, Chip, Stack, TableCell, TableRow, Tooltip } from '@mui/material';
import {
  CheckCircleOutline,
  CalendarMonthOutlined,
  ErrorOutline,
  HelpOutline,
  ReplayOutlined,
  ScheduleOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

import { toUsdDisplay } from '@utils/money';
import { OrderStatus } from '@models/order';
import { UserGroup, type User } from '@models/user';
import type { OrderResponse } from '@apis/order';

import { getOrderStatusMeta } from '@constants/orderLabels';
import { utcToZonedTime, format as formatTz } from 'date-fns-tz';

import {
  StandardTableFrame,
  StandardTableToolbar,
  StandardPagination,
  TableRowStatus,
  cellSx,
} from '@components/common/table';
import { useListReturnNavigation } from '@hooks/useListReturnNavigation';
import { getProductSaleTypeDisplayMeta } from '@components/product/ProductSaleTypeDisplay';
import { getProductTypeDisplayMeta } from '@components/product/PartnerProductSpecDisplays';

const OrderAttributeIcon = ({
  label,
  color,
  icon,
}: {
  label: string;
  color: string;
  icon: React.ReactNode;
}) => (
  <Tooltip title={label} arrow>
    <Box
      component="span"
      aria-label={label}
      sx={{
        color,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 20,
        flexShrink: 0,
        borderRadius: 0.5,
      }}
    >
      {icon ?? <HelpOutline sx={{ fontSize: 16 }} />}
    </Box>
  </Tooltip>
);

const getOrderStatusDisplay = (status: OrderStatus | string) => {
  const meta = getOrderStatusMeta(status);

  switch (status) {
    case OrderStatus.OK:
      return { ...meta, color: 'success.main', icon: <CheckCircleOutline /> };
    case OrderStatus.PENDING:
      return { ...meta, color: 'warning.main', icon: <ScheduleOutlined /> };
    case OrderStatus.FAILED:
      return { ...meta, color: 'error.main', icon: <ErrorOutline /> };
    case OrderStatus.FULL_REFUNDED:
    case OrderStatus.PARTIAL_REFUNDED:
      return { ...meta, color: 'warning.main', icon: <ReplayOutlined /> };
    default:
      return { ...meta, label: meta.label || 'Unknown', color: 'text.secondary', icon: <HelpOutline /> };
  }
};

const getOrderRowStatus = (order: OrderResponse): TableRowStatus => {
  switch (order.orderStatus) {
    case OrderStatus.FULL_REFUNDED:
    case OrderStatus.PARTIAL_REFUNDED:
      return 'softWarning';
    case OrderStatus.FAILED:
      return 'softError';
    default:
      return 'normal';
  }
};

/**
 * Format reserved date as yyyy.MM.dd in KST.
 * Returns an empty string when value is empty.
 */
const formatKSTDateOnly = (ts?: number | null) => {
  if (!ts) return '';
  const d = new Date(ts);
  const kst = utcToZonedTime(d, 'Asia/Seoul');
  return formatTz(kst, 'yyyy.MM.dd');
};

/**
 * Format an order date into KST date and time parts for the table display.
 */
const formatKSTDateTime = (ts?: number | null) => {
  if (!ts) return null;
  const d = new Date(ts);
  const kst = utcToZonedTime(d, 'Asia/Seoul');
  return {
    date: formatTz(kst, 'yyyy.MM.dd'),
    time: formatTz(kst, 'HH:mm:ss'),
  };
};

interface OrdersTableProps {
  data: OrderResponse[];
  isLoading: boolean;
  totalCount?: number;
  page: number;
  pageSize: number;
  onChangePage: (event: React.ChangeEvent<unknown>, value: number) => void;
  user: User | null | undefined;
}

const OrdersTable = ({
  data,
  isLoading,
  totalCount,
  page,
  pageSize,
  onChangePage,
  user,
}: OrdersTableProps) => {
  const { navigateFromList } = useListReturnNavigation();

  const rows = data ?? [];
  const isPartner = user?.group === UserGroup.PARTNER;

  // Partner view hides Stock Code column; status is shown beside Order Code.
  const columnsCount = useMemo(() => (isPartner ? 9 : 10), [isPartner]);

  const handleChangePage = useCallback(
    (next: number) => onChangePage({} as any, next),
    [onChangePage],
  );

  const goDetail = useCallback(
    (orderId: number) => {
      if (!Number.isFinite(orderId) || orderId <= 0) return;
      navigateFromList(`/orders/${orderId}`);
    },
    [navigateFromList],
  );

  return (
    <Box>
      <StandardTableToolbar
        page={page}
        pageSize={pageSize}
        totalCount={totalCount ?? 0}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      <Box mt={1}>
        <StandardTableFrame<OrderResponse>
          head={
            <TableRow>
              <TableCell width={80} align="right">
                #
              </TableCell>
              <TableCell align="left">Order Code</TableCell>
              <TableCell align="left">Product Code</TableCell>
              <TableCell align="left">Product Name</TableCell>

              {!isPartner && <TableCell align="left">Stock Code</TableCell>}

              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="center">Reserved</TableCell>
              <TableCell align="center">Ordered At</TableCell>
              <TableCell align="left">Partner</TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(order) => order.orderId}
          getRowStatus={getOrderRowStatus}
          columnsCount={columnsCount}
          isLoading={isLoading}
          loadingVariant="skeleton"
          skeletonRows={6}
          emptyText="No orders found."
          minWidth={isPartner ? 1050 : 1170}
          renderRow={(order, index) => {
            const orderId = Number(order.orderId);
            const canNavigate = Number.isFinite(orderId) && orderId > 0;

            const displayIndex = (page - 1) * pageSize + index + 1;
            const statusDisplay = getOrderStatusDisplay(order.orderStatus);
            const saleTypeMeta = getProductSaleTypeDisplayMeta(order.product.productSaleType);
            const productTypeMeta = getProductTypeDisplayMeta(order.product.productType);
            const reservedDate = formatKSTDateOnly(order.reservedAt);
            const orderedAt = formatKSTDateTime(order.createdAt);

            return (
              <>
                {/* Index */}
                <TableCell
                  align="right"
                  sx={{
                    ...cellSx.number,
                    color: 'text.secondary',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {displayIndex.toLocaleString('en-US')}
                </TableCell>

                {/* Order Code and status (clickable) */}
                <TableCell
                  align="left"
                  sx={[
                    {
                      cursor: canNavigate ? 'pointer' : 'default',
                      '&:hover .order-code': canNavigate ? { color: 'primary.dark' } : undefined,
                    },
                  ]}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canNavigate) return;
                    goDetail(orderId);
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Box
                      component="span"
                      className="order-code"
                      sx={{
                        ...cellSx.mono,
                        color: canNavigate ? 'primary.main' : 'text.primary',
                        fontWeight: 600,
                        textDecoration: canNavigate ? 'underline' : 'none',
                        textUnderlineOffset: canNavigate ? '3px' : undefined,
                      }}
                    >
                      {order.orderCode}
                    </Box>
                    <Tooltip title={statusDisplay.label} arrow>
                      <Stack
                        component="span"
                        direction="row"
                        spacing={0.4}
                        alignItems="center"
                        sx={{ color: statusDisplay.color, fontSize: 12, fontWeight: 700 }}
                      >
                        {React.cloneElement(statusDisplay.icon, { sx: { fontSize: 16 } })}
                        <Box component="span">{statusDisplay.label}</Box>
                      </Stack>
                    </Tooltip>
                  </Stack>
                </TableCell>

                <TableCell align="left" sx={cellSx.mono}>
                  {order.product.productCode}
                </TableCell>

                <TableCell align="left" sx={cellSx.nowrap}>
                  {order.product.productName}
                </TableCell>

                {!isPartner && (
                  <TableCell align="left" sx={cellSx.nowrap}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <OrderAttributeIcon
                        {...saleTypeMeta}
                        label={`Order type: ${saleTypeMeta.label === '-' ? 'Unknown' : saleTypeMeta.label}`}
                      />
                      <OrderAttributeIcon
                        {...productTypeMeta}
                        label={`Product type: ${productTypeMeta.label === '-' ? 'Unknown' : productTypeMeta.label}`}
                      />
                      <Box component="span" sx={{ ...cellSx.mono, color: 'text.primary' }}>
                        {order.stock.stockCode}
                      </Box>
                    </Stack>
                  </TableCell>
                )}

                <TableCell align="right" sx={cellSx.number}>
                  {order.quantity}
                </TableCell>

                <TableCell align="right" sx={cellSx.money}>
                  {toUsdDisplay(order.paymentAmount)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {reservedDate ? (
                    <Tooltip title="Reserved date" arrow>
                      <Stack
                        component="span"
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={(theme) => ({
                          display: 'inline-flex',
                          minHeight: 24,
                          px: 0.75,
                          borderRadius: 1,
                          backgroundColor: alpha(
                            theme.palette.secondary.main,
                            theme.palette.mode === 'light' ? 0.1 : 0.22,
                          ),
                          color: 'secondary.main',
                          fontSize: 12,
                          fontWeight: 700,
                        })}
                      >
                        <CalendarMonthOutlined sx={{ fontSize: 15 }} />
                        <Box component="span" sx={cellSx.number}>
                          {reservedDate}
                        </Box>
                      </Stack>
                    </Tooltip>
                  ) : (
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {'\u2014'}
                    </Box>
                  )}
                </TableCell>

                <TableCell align="center" sx={{ ...cellSx.nowrap, py: 0.75 }}>
                  {orderedAt ? (
                    <Stack component="span" spacing={0} alignItems="center" sx={{ lineHeight: 1.25 }}>
                      <Box component="span" sx={{ ...cellSx.number, color: 'text.secondary', fontSize: 12 }}>
                        {orderedAt.date}
                      </Box>
                      <Box component="span" sx={{ ...cellSx.mono, fontSize: 12, fontWeight: 700 }}>
                        {orderedAt.time} KST
                      </Box>
                    </Stack>
                  ) : (
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {'\u2014'}
                    </Box>
                  )}
                </TableCell>

                <TableCell align="left" sx={cellSx.nowrap}>
                  <Chip
                    label={order.partner.name}
                    size="small"
                    sx={(theme) => ({
                      height: 24,
                      borderRadius: 1,
                      backgroundColor: alpha(
                        theme.palette.primary.main,
                        theme.palette.mode === 'light' ? 0.1 : 0.22,
                      ),
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      letterSpacing: 0.1,
                      '& .MuiChip-label': { px: 1 },
                    })}
                  />
                </TableCell>

              </>
            );
          }}
        />
      </Box>

      <Box mt={2}>
        <StandardPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount ?? 0}
          onChangePage={handleChangePage}
          size="medium"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default OrdersTable;
